import { useState, useEffect } from 'react'
import { Card, Form, Button, Alert, Row, Col, ListGroup } from 'react-bootstrap'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { toast } from 'react-toastify'

export const AccountSettingsPage = () => {
  const { user, updateUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Profile form
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  })

  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    ticketUpdates: true,
    statusChanges: true,
    comments: true
  })

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || ''
      })
    }
  }, [user])

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await api.patch('/users/profile', profileData)
      updateUser(response.data.data)
      toast.success('Profile updated successfully!')
      setSuccess('Profile updated successfully!')
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to update profile'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    setLoading(true)

    try {
      await api.patch('/users/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })
      toast.success('Password changed successfully!')
      setSuccess('Password changed successfully!')
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to change password'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // This would call an API endpoint to save notification preferences
      // For now, just show success
      toast.success('Notification preferences saved!')
      setSuccess('Notification preferences saved!')
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to save preferences'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="account-settings-page">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">
          <i className="fas fa-cog me-2 text-primary"></i>
          Account Settings
        </h2>
        <p className="text-muted mb-0">
          Manage your account preferences and security settings
        </p>
      </div>

      <Row>
        <Col lg={3}>
          {/* Settings Menu */}
          <Card className="border-0 shadow-sm mb-4">
            <ListGroup variant="flush">
              <ListGroup.Item
                action
                active={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
                className="d-flex align-items-center"
              >
                <i className="fas fa-user me-3"></i>
                Profile
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeTab === 'password'}
                onClick={() => setActiveTab('password')}
                className="d-flex align-items-center"
              >
                <i className="fas fa-lock me-3"></i>
                Password
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeTab === 'notifications'}
                onClick={() => setActiveTab('notifications')}
                className="d-flex align-items-center"
              >
                <i className="fas fa-bell me-3"></i>
                Notifications
              </ListGroup.Item>
            </ListGroup>
          </Card>

          {/* User Info Card */}
          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="text-center mb-3">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              </div>
              <h6 className="text-center mb-1">{user?.name}</h6>
              <p className="text-muted text-center small mb-2">{user?.email}</p>
              <p className="text-center mb-0">
                <span className="badge bg-primary">{user?.role}</span>
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9}>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-4">
              {success}
            </Alert>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-user me-2 text-primary"></i>
                  Profile Information
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleProfileSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Organization</Form.Label>
                    <Form.Control
                      type="text"
                      value={user?.org?.name || 'N/A'}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Role</Form.Label>
                    <Form.Control
                      type="text"
                      value={user?.role || 'N/A'}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-lock me-2 text-primary"></i>
                  Change Password
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handlePasswordSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={8}
                    />
                    <Form.Text className="text-muted">
                      Must be at least 8 characters long
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Confirm New Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? 'Changing...' : 'Change Password'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-bell me-2 text-primary"></i>
                  Notification Preferences
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleNotificationSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="emailNotifications"
                      label="Email Notifications"
                      checked={notifications.emailNotifications}
                      onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block ms-4">
                      Receive email notifications for important updates
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="ticketUpdates"
                      label="Ticket Updates"
                      checked={notifications.ticketUpdates}
                      onChange={(e) => setNotifications({ ...notifications, ticketUpdates: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block ms-4">
                      Get notified when your tickets are updated
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="statusChanges"
                      label="Status Changes"
                      checked={notifications.statusChanges}
                      onChange={(e) => setNotifications({ ...notifications, statusChanges: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block ms-4">
                      Receive notifications when ticket status changes
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="comments"
                      label="Comments"
                      checked={notifications.comments}
                      onChange={(e) => setNotifications({ ...notifications, comments: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block ms-4">
                      Get notified when someone comments on your tickets
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Preferences'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  )
}





